import { AssociationService } from "@/service/AssociationService";
import { generateCacheKey } from "@/util/associationUtils";
import { ASSOCIATION_CONSTANTS } from "@/util/constants";
import { hasContactFields } from "@/util/customObjectUtils";

/**
 * Minimal shape of the store required for ensuring valid contact association
 */
interface StoreLike {
  contactAssociationSettings: {
    selectedAssociation?: string[] | string;
    objectModel?: string;
  } | null;
  currentCustomObjectModel: string | null;
  locationId: string;
  elements: any[];
  associationCache: Map<string, any>;
  anyUnsavedChanges: boolean;
}

/**
 * Extract an array of association ids from a heterogeneous associations array
 * Accepts entries in either cached option shape ({ value }) or API shape ({ id })
 */
const extractAssociationIds = (associations: any[]): string[] => {
  if (!Array.isArray(associations)) {
    return [];
  }
  return associations
    .map((item) => (item?.value ? String(item.value) : item?.id ? String(item.id) : ""))
    .filter((id) => Boolean(id)) as string[];
};

/**
 * Ensure the saved contact association remains valid with latest associations.
 * If the saved association was deleted externally, pick the first available association
 * for the current custom object model and persist it.
 *
 * @param store - Pinia app store subset
 * @returns Promise<void>
 */
export const ensureValidContactAssociation = async (store: StoreLike): Promise<void> => {
  const saved = store.contactAssociationSettings;
  const currentModel = store.currentCustomObjectModel || saved?.objectModel;
  if (!currentModel || !store.locationId) {
    return;
  }

  // Only enforce when there are contact fields present; otherwise allow none
  const hasContacts = hasContactFields(store.elements, null);
  if (!hasContacts) {
    return;
  }

  // If no saved selection yet, nothing to validate here
  const savedSelection = Array.isArray(saved?.selectedAssociation)
    ? saved?.selectedAssociation?.[0]
    : (saved?.selectedAssociation as string | undefined);

  // Load associations from cache or API
  const cacheKey = generateCacheKey(currentModel, store.locationId);
  const cachedData = store.associationCache.get(cacheKey);
  const isCacheExpired =
    cachedData && Date.now() - cachedData.timestamp > ASSOCIATION_CONSTANTS.CACHE_EXPIRY_MS;

  let optionIds: string[] = [];
  if (cachedData && !isCacheExpired) {
    optionIds = extractAssociationIds(cachedData.associations || []);
  } else {
    try {
      const response = await AssociationService.getAssociations(
        currentModel,
        store.locationId,
        ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE
      );
      const associations = response.data?.associations || [];
      optionIds = extractAssociationIds(associations);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch associations while ensuring validity:", err);
      return;
    }
  }

  if (!optionIds.length) {
    return;
  }

  const exists = savedSelection && optionIds.includes(String(savedSelection));
  if (exists) {
    return;
  }

  // Saved selection missing or invalid -> pick first option and persist
  const nextValue = optionIds[0];
  if (nextValue) {
    store.contactAssociationSettings = {
      selectedAssociation: [nextValue],
      objectModel: currentModel,
    } as any;
    store.anyUnsavedChanges = true;
  }
};
